import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import Banner from "../../common/components/banner";
import banner from "../../assets/images/banners/banner_genhi.png";
import InterviewService from "../../services/InterviewService";
import { InterviewSummary } from "../../types/Interview";
import publicarAplicados from "../publicarAplicados";
import WizardEntrevista, { wizardJaDispensado } from "./WizardEntrevista";
import palette from "../../common/styles/palette";

/**
 * As Entrevistas do professor logado. Existe porque a Entrevista sobrevive ao
 * fechamento do navegador: o Rascunho está no servidor desde o turno 1, e sem
 * uma lista não haveria como voltar a ele.
 */
export default function List() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [entrevistas, setEntrevistas] = useState<InterviewSummary[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  // Tour de boas-vindas: aparece na primeira visita à área (a menos que o
  // professor tenha marcado "não mostrar novamente" — ver WizardEntrevista).
  const [mostrarWizard, setMostrarWizard] = useState(() => !wizardJaDispensado());

  const [duplicando, setDuplicando] = useState(false);
  const [publicandoId, setPublicandoId] = useState<string | null>(null);
  const [publicados, setPublicados] = useState<string[]>([]);
  // Confirmação obrigatória: descartar é destrutivo do ponto de vista do
  // professor, mesmo sendo lógico no banco. Este app já dispara ação
  // destrutiva sem perguntar em outro lugar; aqui não repetimos.
  const [aDescartar, setADescartar] = useState<InterviewSummary | null>(null);
  const [descartando, setDescartando] = useState(false);

  const carregar = async () => {
    const r = await InterviewService.mine();
    if (r.kind === "ok") {
      setEntrevistas(r.items);
      setErro(null);
    } else {
      setErro(r.msg);
    }
    setCarregando(false);
  };

  useEffect(() => {
    carregar();
  }, []);

  const confirmarDescarte = async () => {
    if (!aDescartar) return;
    setDescartando(true);
    const r = await InterviewService.discard(aDescartar.interview_id);
    setDescartando(false);
    setADescartar(null);

    if (r.kind === "failed") {
      setErro(r.msg);
      toast(r.msg, { type: "error" });
      return;
    }

    toast(t("interview_discarded"), { type: "success" });
    await carregar();
  };

  const duplicar = async (e: InterviewSummary) => {
    setDuplicando(true);
    const r = await InterviewService.duplicate(e.interview_id);
    setDuplicando(false);

    if (r.kind !== "ok") {
      toast(r.kind === "conflict" ? r.msg : r.msg, { type: "error" });
      return;
    }

    // Leva direto para a cópia: é nela que ele vai trabalhar agora.
    toast(t("interview_duplicated"), { type: "success" });
    navigate(`/interview/detail/${r.state.interview_id}`);
  };

  const publicar = async (e: InterviewSummary) => {
    if (!e.applied || publicandoId) return;
    setPublicandoId(e.interview_id);
    try {
      await publicarAplicados(e.applied);
      setPublicados((atuais) => [...atuais, e.interview_id]);
      toast(t("interview_published"), { type: "success" });
    } catch (error: any) {
      toast(error?.response?.data?.Msg || t("interview_publish_error"), {
        type: "error",
      });
    } finally {
      setPublicandoId(null);
    }
  };

  return (
    <Box>
      <WizardEntrevista
        aberto={mostrarWizard}
        aoFechar={() => setMostrarWizard(false)}
      />
      <Banner
        type="medium"
        image={banner}
        title={t("banner_title_interview")}
        subtitle={t("banner_subtitle_interview")}
      />
      <Box
        sx={{
          position: "relative",
          width: "100%",
          py: 6,
          px: { xs: 2, sm: 4, md: 8 },
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Button variant="contained" onClick={() => navigate("/interview/detail")}>
            {t("interview_new")}
          </Button>
          <Typography variant="caption" sx={{ ml: 2, color: palette.textMuted }}>
            {t("interview_new_hint")}
          </Typography>
        </Box>

        {erro && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}

        {carregando ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              mt: 3,
              overflowX: { xs: "visible", md: "auto" },
              boxShadow: { xs: "none", md: 1 },
              bgcolor: { xs: "transparent", md: palette.surface },
            }}
          >
            <Table
              sx={{
                minWidth: { xs: 0, md: 870 },
                tableLayout: { xs: "auto", md: "fixed" },
                display: { xs: "block", md: "table" },
              }}
            >
              <TableHead sx={{ display: { xs: "none", md: "table-header-group" } }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: 230 }}>
                    {t("interview_col_scenario")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: 110 }}>
                    {t("interview_col_status")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: 110 }}>
                    {t("interview_col_gaps")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: 140 }}>
                    {t("interview_col_updated")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: 280 }}>
                    {t("table_label_button")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ display: { xs: "block", md: "table-row-group" } }}>
                {entrevistas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" sx={{ color: palette.textMuted }}>
                        {t("interview_list_empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {entrevistas.map((e) => (
                  <TableRow
                    key={e.interview_id}
                    sx={{
                      display: { xs: "grid", md: "table-row" },
                      gridTemplateColumns: { xs: "1fr 1fr" },
                      gap: { xs: 1.5, md: 0 },
                      p: { xs: 2, md: 0 },
                      mb: { xs: 1.5, md: 0 },
                      border: { xs: `1px solid ${palette.border}`, md: "none" },
                      borderRadius: { xs: 2, md: 0 },
                      bgcolor: { xs: palette.surface, md: "transparent" },
                      boxShadow: { xs: "0 1px 3px rgba(15, 23, 42, 0.08)", md: "none" },
                      "& > .MuiTableCell-root": {
                        p: { xs: 0, md: 2 },
                        borderBottom: {
                          xs: "none",
                          md: "1px solid rgba(224, 224, 224, 1)",
                        },
                      },
                    }}
                  >
                    <TableCell sx={{ gridColumn: { xs: "1 / -1" } }}>
                      <Typography
                        variant="caption"
                        sx={{ display: { xs: "block", md: "none" }, color: palette.textMuted, mb: 0.5 }}
                      >
                        {t("interview_col_scenario")}
                      </Typography>
                      <Typography variant="body2">
                        {e.name || t("interview_unnamed")}
                      </Typography>
                      {/* Duas Entrevistas de teste nascem com o mesmo nome e só
                          o horário as distinguia. Isto diz em que pé cada uma
                          está: quanto já foi escrito e o que ela espera ouvir. */}
                      <Typography variant="caption" sx={{ color: palette.textMuted }}>
                        {e.n_items > 0
                          ? `${e.n_skills} ${t("interview_col_sections")} · ${e.n_items} ${t("interview_items")}`
                          : t("interview_nothing_written")}
                      </Typography>
                      {e.next_question && (
                        <Typography
                          variant="caption"
                          title={e.next_question}
                          sx={{
                            display: "block",
                            color: palette.textMuted,
                            fontStyle: "italic",
                            maxWidth: 320,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {e.next_question}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ display: { xs: "block", md: "none" }, color: palette.textMuted, mb: 0.5 }}
                      >
                        {t("interview_col_status")}
                      </Typography>
                      <Chip
                        size="small"
                        variant={e.status === "aplicada" ? "filled" : "outlined"}
                        color={e.status === "aplicada" ? "success" : "default"}
                        label={t(`interview_status_${e.status}`)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ display: { xs: "block", md: "none" }, color: palette.textMuted, mb: 0.5 }}
                      >
                        {t("interview_col_gaps")}
                      </Typography>
                      {e.status === "aplicada" ? (
                        <Typography variant="body2" sx={{ color: palette.textMuted }}>
                          —
                        </Typography>
                      ) : (
                        <>
                          {e.blocking_gaps > 0 ? (
                            <Typography variant="body2" sx={{ color: palette.dangerText }}>
                              {e.blocking_gaps} {t("interview_blocking")}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: palette.emeraldDeep }}>
                              {t("interview_ready_to_apply")}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: palette.textMuted }}>
                            {e.open_gaps} {t("interview_open_gaps")}
                          </Typography>
                        </>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{ gridColumn: { xs: "1 / -1" }, whiteSpace: "nowrap" }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ display: { xs: "block", md: "none" }, color: palette.textMuted, mb: 0.5 }}
                      >
                        {t("interview_col_updated")}
                      </Typography>
                      {new Date(e.updated_at).toLocaleString("pt-BR")}
                    </TableCell>
                    <TableCell
                      sx={{
                        gridColumn: { xs: "1 / -1" },
                        width: { xs: "auto", md: 280 },
                        verticalAlign: "top",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ display: { xs: "block", md: "none" }, color: palette.textMuted, mb: 0.5 }}
                      >
                        {t("table_label_button")}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          "& .MuiButton-root": { whiteSpace: "nowrap" },
                        }}
                      >
                      <Button
                        size="small"
                        onClick={() => navigate(`/interview/detail/${e.interview_id}`)}
                      >
                        {e.status === "aplicada"
                          ? t("interview_see")
                          : t("interview_resume")}
                      </Button>
                      {/* Duplicar vale para qualquer uma: é o "faz igual, mas
                          para pneumonia" de quem monta uma série de estações. */}
                      <Button size="small" disabled={duplicando} onClick={() => duplicar(e)}>
                        {t("interview_duplicate")}
                      </Button>
                      {e.status === "aplicada" && e.applied && (
                        <Button
                          size="small"
                          disabled={!!publicandoId || publicados.includes(e.interview_id)}
                          onClick={() => publicar(e)}
                          sx={{ color: palette.emeraldDeep }}
                        >
                          {publicandoId === e.interview_id
                            ? t("interview_publishing")
                            : publicados.includes(e.interview_id)
                            ? t("interview_published_label")
                            : t("interview_publish")}
                        </Button>
                      )}
                      {/* Aplicada não se descarta: ela é o registro de autoria
                          do que criou. O botão some em vez de aparecer apagado —
                          oferecer e negar seria pior que não oferecer. */}
                      {e.status !== "aplicada" && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setADescartar(e)}
                        >
                          {t("interview_discard")}
                        </Button>
                      )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Dialog open={aDescartar !== null} onClose={() => setADescartar(null)}>
        <DialogTitle>{t("interview_discard_title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("interview_discard_body")}
            {aDescartar?.name && <> <strong>{aDescartar.name}</strong></>}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5, fontSize: 13, color: palette.textMuted }}>
            {t("interview_discard_note")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={descartando} onClick={() => setADescartar(null)}>
            {t("interview_form_cancel")}
          </Button>
          <Button color="error" variant="contained" disabled={descartando} onClick={confirmarDescarte}>
            {descartando ? t("interview_discarding") : t("interview_discard")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
